'use client'

import React, { FC, ReactElement } from 'react'
import styles from './VerifyBrc20.module.css'
import useInscription from '@/utils/hooks/useInscription'
import useInscriptionOutpoint from '@/utils/hooks/useInscriptionOutpoint'
import useInscriptionContent from '@/utils/hooks/useInscriptionContent'
import classNames from 'classnames'
import { Inscription } from '@/utils/types/inscription'
import AlertIcon from '@/icons/alert'
import CheckmarkCircleIcon from '@/icons/checkmark-circle'
import DisclaimerModal from '@/components/brc20-disclaimer-modal'
import useBestinslotBrc20Valid from '@/utils/hooks/useBestinslotBrc20Valid'
import { BEST_IN_SLOT } from '@/utils/constants'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const VerifyBrc20: FC<{ inscription: Inscription }> = (props): ReactElement => {
	const { data: outpoint } = useInscriptionOutpoint(props?.inscription?.id)
	const { data: inscription, isLoading } = useInscription(props?.inscription?.id)
	const content = useInscriptionContent(props?.inscription?.id)
	const bestinslotData = useBestinslotBrc20Valid(inscription?.brc20?.ticker ? inscription.id : undefined)
	const [open, setOpen] = React.useState(false)

	const content_type = props?.inscription?.content_type
	const possibleBrc20 =
		content_type?.includes('text/plain') || content_type?.includes('application/json')

	if (!possibleBrc20) {
		return <React.Fragment />
	}

	const isBrc20 = JSON.stringify(content)?.includes('brc-20')

	if (!isBrc20 || isLoading) {
		return <React.Fragment />
	}

	const isVerified = inscription?.brc20?.ticker

	if (!isVerified) {
		return <React.Fragment />
	}

	if (bestinslotData?.isLoading) {
		return <React.Fragment />
	}

	const handleClick = () => {
		if (bestinslotData?.valid) {
			window.open(`${BEST_IN_SLOT}/ordinals/inscription/${inscription?.num}`, '_blank')
		} else {
			setOpen(true)
		}
	}

	return (
		<>
			<div
				className={styles.root}
				onClick={handleClick}
				role="button"
				tabIndex={0}
				onKeyDown={onKeyboardClick(handleClick)}
			>
				<div>
					{bestinslotData?.valid ? (
						<CheckmarkCircleIcon className={styles.icon} />
					) : (
						<AlertIcon className={styles.icon} />
					)}
				</div>
				<p className={classNames('text-sm', 'regular')}>
					{bestinslotData?.valid
						? 'This BRC-20 has been verified by Ordinals Wallet and Bestinslot.xyz. Double check by clicking here.'
						: 'This BRC-20 needs to be verified by purchaser. Please verify the tokens are valid by clicking here.'}
				</p>
			</div>
			<DisclaimerModal
				open={open}
				onClose={() => setOpen(false)}
				inscription={inscription}
				owner={outpoint?.owner}
			/>
		</>
	)
}

export default VerifyBrc20
